import React, { ButtonHTMLAttributes } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useSelector } from 'react-redux';
import Button from '../buttons/Button';
import { findReactElement, replaceReactElement } from '../../../utils/reactUtils';
import { RootState } from '../../../redux/reducers';
import { BUSINESS_URL, COMMUNITY_URL } from '../../../constants';

function getFrontendUrl(): string {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }
  return process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS' ? BUSINESS_URL : COMMUNITY_URL;
}
interface CheckoutFormProps {
  clientSecret: string;
  disabled: boolean;
  setDisabled: any;
  onSubmit: any;
  redirectQueryParams?: string;
  returnPath?: string;
  intentType?: string;
  className?: string;
  containerClassName?: string;
  buttonClassName?: string;
  children?: React.ReactNode;
}

export default function CheckoutForm({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  clientSecret,
  onSubmit,
  disabled,
  setDisabled,
  className = '',
  redirectQueryParams = '',
  returnPath = '/payment/completion/',
  intentType = 'payment',
  containerClassName = '',
  buttonClassName = '',
  children,
}: CheckoutFormProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { firstName, lastName } = useSelector((state: RootState) => state.application);

  const handleSubmit = async (e: any): Promise<void> => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setDisabled(true);
    try {
      await onSubmit();
    } catch (error) {
      setDisabled(false);
      return;
    }
    let error;
    if (intentType === 'setup' || intentType === 'setupChange') {
      ({ error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${getFrontendUrl()}${returnPath}${redirectQueryParams}`,
        },
      }));
    } else {
      ({ error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${getFrontendUrl()}${returnPath}${redirectQueryParams}`,
        },
      }));
    }
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        toast.error(error.message!);
      } else {
        toast.error(t('toast.error.unexpected'));
      }
    }
    setDisabled(false);
  };

  const paymentElementOptions = {
    layout: 'tabs',
    loader: 'always',
    defaultValues: {
      billingDetails: {
        name: `${firstName} ${lastName}`,
      },
    },
  } as StripePaymentElementOptions;

  const button = findReactElement(children, CheckoutForm.CheckoutButton);
  if (button) {
    children = replaceReactElement(
      children,
      button,
      React.cloneElement(button, {
        onClick: handleSubmit,
      }),
    );
  }

  return (
    <div
      data-testid="checkout-form"
      id="payment-form relative"
      className={twMerge('w-full', className)}>
      <PaymentElement
        data-testid="stripe-form"
        className={twMerge('my-4 mb-8 w-full rounded-[12px] p-4', containerClassName)}
        options={paymentElementOptions}
      />
      {children ?? (
        <div className="flex justify-end">
          <Button
            variant="primary"
            className={buttonClassName}
            disabled={disabled}
            onClick={handleSubmit}>
            {intentType === 'setupChange'
              ? t('page.payment.confirm')
              : t('page.payment.payAndConfirm')}
          </Button>
        </div>
      )}
    </div>
  );
}

// props of Button using typeof Button
function CheckoutButton(props: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  return <Button variant="primary" {...props} />;
}

CheckoutForm.CheckoutButton = CheckoutButton;
